import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { Container, Button, Form, Row, Col } from 'react-bootstrap';
import ReactSelect from 'react-select';
import { addAJobInitialValues, addAJobSchema } from 'constants/schemaValidations';
import { useFormik } from 'formik';
import {
	useCheckSubscriptionQuery,
	useJobCommonFieldsQuery,
	useJobEditQuery,
	useJobUpdateMutation,
	useSaveNewJobMutation,
	useSearchAddress,
} from 'services';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';
import useTranslate from 'hooks/useTranslate';
import { AddAJobInterface } from 'constants/interfaces';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import EditJobSekeleton from 'components/Common/Skeleton/EditJobSekeleton';
import useAuthStore from 'store/auth';
import useCommonStore, { setModalConfig } from 'store/common';
import { JobAiSuggestions } from './JobAISuggestions';
import { getCommonObjects } from 'utils';

export const PostNewJob: React.FC = () => {
	const { id } = useParams<any>();
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const { user: authUser }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const [locationKeyword, setLocationKeyword] = useState<any>('');

	// mutation
	const { data: { data: savedData = {}, success } = {}, isPending: fetchingData } =
		useJobEditQuery(id);
	const { mutateAsync: saveNewJobMutation, isPending } = useSaveNewJobMutation();
	const { mutateAsync: JobUpdateMutation } = useJobUpdateMutation();
	const { data: commonFields = [] } = useJobCommonFieldsQuery();
	const { data: checkSubscription = [] } = useCheckSubscriptionQuery('post-job');

	const { hideCommonModal }: any = useCommonStore((state) => state);
	const { data: locationData = [] } = useSearchAddress({
		keyword: locationKeyword,
		type: false,
	});

	useEffect(() => {
		if (checkSubscription?.success === false) {
			setModalConfig({
				visible: true,
				type: 'subscriptionAlertForPostJob',
				onClose: () => {
					navigate(-1);
					hideCommonModal();
				},
				onClick: () => {
					navigate('/company/subscriptions');
					hideCommonModal();
				},
			});
		}
	}, [checkSubscription]);

	useEffect(() => {
		if (id && !fetchingData && (!success || (success && savedData?.userId !== authUser?.userId))) {
			navigate(-1);
		}
	}, [savedData, fetchingData]);

	const {
		companyType = [],
		industries = [],
		jobType = [],
		languages = [],
		levels = [],
		remoteOption = [],
		roles = [],
		salarytype = [],
		skill = [],
	} = commonFields;

	const getInitialData = () => {
		return !id
			? {
					...addAJobInitialValues,
					companyType: [],
					industries: [],
					jobType: '',
					languages: [],
					levels: [],
					remoteOption: [],
					roles: [],
					salarytype: [],
					skill: [],
					countryId: authUser?.countryId,
					stateId: authUser?.stateId,
					cityId: authUser?.cityId,
					location: authUser?.address,
				}
			: {
					...addAJobInitialValues,
					...savedData,
					jobRole: savedData?.jobRole?.map((data: any) => data?.id) || [],
					language: savedData?.language?.map((data: any) => data?.value) || [],
					skills: savedData?.skills?.map((data: any) => data?.id?.toString()) || [],
				};
	};

	const formik = useFormik({
		initialValues: getInitialData(),
		validationSchema: addAJobSchema(translate),
		enableReinitialize: true,
		onSubmit: async (value) => {
			const values = { ...value, description: value?.description.trim() };
			if (id) {
				try {
					const res = await JobUpdateMutation({ id, values });
					if (res?.success) {
						toast.success(res?.message);
						navigate(`/job-detail/${id}`);
					} else {
						// Optionally, handle the case where the update was not successful.
						toast.error(res?.message || 'An error occurred');
					}
				} catch (error) {
					// Handle any errors that occur during the mutation
					console.error('Error updating job:', error);
					toast.error(translate('ToastMessage.anUnexpectedErrorOccurred'));
				}
			} else {
				setModalConfig({
					visible: true,
					type: 'confirmModal',
					onClick: () => {
						navigate(`/company/create-questionnaire`, { state: { formValues: value } });
						hideCommonModal();
					},
					onClose: () => onHandleNoClick(),
					data: { title: translate('Modal.wantToAddQuestionnaire') },
					props: {
						dialogClassName: 'confirm-modal',
					},
				});
			}
		},
	});

	const onHandleNoClick = () => {
		saveNewJobMutation(formik?.values)
			.then((res: any) => {
				if (res?.success) {
					formik.resetForm();
					setModalConfig({
						visible: true,
						type: 'successModal',
					});
				} else {
					hideCommonModal();
				}
			})
			.catch(() => {
				hideCommonModal();
			});
	};

	const renderError = <T extends keyof AddAJobInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const getNameById = (arr: any, id: any, key: any) => {
		if (arr?.length) {
			return arr.find((item: any) => item?.id === Number(id))?.[key];
		}
	};

	const handleOpenAIResponse = useCallback(
		(aiData: any) => {
			const myData = JSON.parse(aiData);
			const data = myData.jobpost;
			if (data) {
				const ids: any = getCommonObjects(roles, data?.roles);

				const langs: any = [];
				data?.languages.forEach((language: any) => {
					langs.push(language.title);
				});

				const skills: any = [];
				data?.skills.forEach((skill: any) => {
					skills.push(skill.id);
				});

				formik.setFieldValue('title', data?.title || '');
				formik.setFieldValue('company', data?.companyType?.value || 'DirectEmployer');
				formik.setFieldValue('description', data?.description || '');
				formik.setFieldValue('industryId', data?.industries?.id);
				formik.setFieldValue('jobType', data?.jobType?.value);
				formik.setFieldValue('language', langs);
				formik.setFieldValue('levelId', data?.levels?.id);
				formik.setFieldValue('workMode', data?.remoteOption?.value);
				formik.setFieldValue('jobRole', ids);
				formik.setFieldValue('salaryType', data?.salarytype?.value);
				formik.setFieldValue('skills', skills);
			}
			setLocationKeyword(authUser?.address);
		},
		[formik],
	);

	useEffect(() => {
		if (locationData?.length > 0) {
			formik.setFieldValue('location', locationData[0]?.location);
			formik.setFieldValue('cityId', locationData[0]?.cityId);
			formik.setFieldValue('stateId', locationData[0]?.stateId);
			formik.setFieldValue('countryId', locationData[0]?.countryId);
		}
	}, [locationData.length]);

	const updateSalaryFunc = (data: string) => {
		const [minSalary, maxSalary] = typeof data === 'string' ? data.split('-') : [1000, 1500];
		setTimeout(() => {
			formik.setFieldValue('minAmount', Number(minSalary) || 1000);
			formik.setFieldValue('maxAmount', Number(maxSalary) || 1500);
		}, 1000);
	};

	return (
		<section className="post-new-job py-50">
			<Container>
				<div className="post-new-job-inner bg-white">
					<h4 className="fw-bold">
						{!id ? translate('Common.add') : translate('Common.edit')} {translate('Job.aJob')}
					</h4>
					<div className="post-new-job-form">
						{id && fetchingData ? (
							<EditJobSekeleton />
						) : (
							<Form onSubmit={formik.handleSubmit}>
								<Row>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.jobTile')}</Form.Label>
											<Form.Control
												type="text"
												placeholder={translate('PlaceHolder.enterJobTitle')}
												className="rounded-pill"
												name="title"
												autoComplete="off"
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													let inputValue = e.target.value;

													// Remove leading spaces if present
													inputValue = inputValue.replace(/^\s+/, '');

													// Allow only one space between words
													inputValue = inputValue.replace(/\s{2,}/g, ' ');
													formik.setFieldValue(`title`, inputValue);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.title}
											/>
											{renderError('title')}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.companyType')}</Form.Label>
											{formik?.values && (
												<ReactSelect
													className="form-react-select"
													classNamePrefix="select"
													placeholder={translate('PlaceHolder.selectCompanyType')}
													autoFocus={false}
													name="company"
													options={companyType}
													getOptionLabel={(option: any) => option?.title}
													getOptionValue={(option: any) => option?.value}
													onChange={(value: any) => {
														formik.setFieldValue('company', value?.value);
													}}
													value={companyType?.find(
														(item: any) => item.value === formik.values.company,
													)}
													onBlur={formik.handleBlur}
												/>
											)}

											{renderError('company')}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.companyIndustry')}</Form.Label>
											<ReactSelect
												className="form-react-select"
												classNamePrefix="select"
												placeholder={translate('PlaceHolder.selectCompanyIndustry')}
												autoFocus={false}
												name="industryId"
												options={industries}
												getOptionLabel={(option: any) => option?.title}
												getOptionValue={(option: any) => option?.id}
												onChange={(value: any) => formik.setFieldValue('industryId', value?.id)}
												value={industries?.find(
													(item: any) => item.id === formik.values.industryId,
												)}
												onBlur={formik.handleBlur}
											/>
											{renderError('industryId')}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.experience')}</Form.Label>
											<ReactSelect
												className="form-react-select"
												classNamePrefix="select"
												placeholder={translate('PlaceHolder.selectExperience')}
												autoFocus={false}
												name="levelId"
												options={levels}
												getOptionLabel={(option: any) => option?.title}
												getOptionValue={(option: any) => option?.id}
												onChange={(value: any) => formik.setFieldValue('levelId', value?.id)}
												value={levels?.find((item: any) => item.id === formik.values.levelId)}
												onBlur={formik.handleBlur}
											/>
											{renderError('levelId')}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.roles')}</Form.Label>
											<ReactSelect
												isSearchable
												name="jobRole"
												options={roles}
												getOptionValue={(option) => option?.id ?? option.value}
												getOptionLabel={(option) => option?.parentName ?? option.label}
												onChange={(optionValue: any) => {
													const valuesArr = [];
													if (formik.values.jobRole?.length) {
														valuesArr.push(...formik.values.jobRole, optionValue?.id?.toString());
													} else {
														valuesArr.push(optionValue?.id?.toString());
													}
													formik.setFieldValue('jobRole', valuesArr);
												}}
												onBlur={formik.handleBlur}
												autoFocus={false}
												className="form-react-select"
												classNamePrefix="select"
												value="Add Roles"
												placeholder={translate('PlaceHolder.addRoles')}
												isOptionDisabled={(option: any) =>
													formik.values.jobRole.some((item: any) => Number(item) === option?.id)
												}
											/>
											{renderError('jobRole')}
										</Form.Group>

										{formik.values.jobRole?.length > 0 && (
											<div className="selected-items-list d-flex flex-wrap gap-3 mb-4">
												{[...formik.values.jobRole].map((roleId: any, index: number) => (
													<div
														key={index}
														className="selected-item-box d-flex align-items-center gap-2 rounded-pill"
													>
														<p className="mb-0">
															{getNameById(roles, roleId, 'parentName') ?? roleId}
														</p>
														<Button
															variant="link"
															className="p-0 text-decoration-none border-0"
															onClick={() => {
																const updatedRoles = formik.values.jobRole.filter(
																	(roleValue: any) => roleValue !== roleId,
																);
																formik.setFieldValue('jobRole', updatedRoles);
															}}
														>
															<i className="ri-close-line"></i>
														</Button>
													</div>
												))}
											</div>
										)}
									</Col>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.language')}</Form.Label>
											<ReactSelect
												isClearable
												isSearchable
												isMulti
												name="language"
												options={languages}
												getOptionValue={(option) => option.value}
												getOptionLabel={(option) => option.title}
												onChange={(selectedOption: any) => {
													const selectedIds = selectedOption.map((option: any) => option.value);
													formik.setFieldValue('language', selectedIds);
												}}
												onBlur={formik.handleBlur}
												value={languages?.filter((language: any) =>
													formik.values.language?.includes(language.value),
												)}
												autoFocus={false}
												placeholder={translate('PlaceHolder.selectLanguage')}
												className="form-react-select"
												classNamePrefix="select"
											/>
											{renderError('language')}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.workMode')}</Form.Label>
											<ReactSelect
												isSearchable
												name="workMode"
												options={remoteOption}
												getOptionValue={(option) => option.value}
												getOptionLabel={(option) => option.title}
												onChange={(optionValue: any) => {
													formik.setFieldValue('workMode', optionValue?.value);
												}}
												onBlur={formik.handleBlur}
												value={remoteOption?.find(
													(category: any) => category.value === formik.values.workMode,
												)}
												autoFocus={false}
												placeholder={translate('PlaceHolder.selectWorkMode')}
												className="form-react-select"
												classNamePrefix="select"
											/>
											{renderError('workMode')}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.employmentType')}</Form.Label>
											<ReactSelect
												isSearchable
												name="jobType"
												options={jobType}
												getOptionValue={(option) => option.value}
												getOptionLabel={(option) => option.title}
												onChange={(optionValue: any) => {
													formik.setFieldValue('jobType', optionValue?.value);
												}}
												onBlur={formik.handleBlur}
												value={jobType?.find(
													(category: any) => category.value === formik.values.jobType,
												)}
												autoFocus={false}
												placeholder={translate('PlaceHolder.selectEmploymentType')}
												className="form-react-select"
												classNamePrefix="select"
											/>
											{renderError('jobType')}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.jobLocation')}</Form.Label>
											{(!id || formik.values.location) && (
												<CustomSearchAddress
													className="form-react-select"
													classNamePrefix="select"
													selectProps={{
														placeholder: translate('PlaceHolder.locationText'),
														onChange: (value: any) => {
															formik.setFieldValue('location', value.location);
															formik.setFieldValue('cityId', value.cityId);
															formik.setFieldValue('stateId', value.stateId);
															formik.setFieldValue('countryId', value.countryId);
														},
														name: 'location',
													}}
													fieldValue={formik.values.location}
													onBlur={formik.handleBlur}
												/>
											)}
											{renderError('location')}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.salaryType')}</Form.Label>
											<ReactSelect
												isSearchable
												name="salaryType"
												options={salarytype}
												getOptionValue={(option) => option.value}
												getOptionLabel={(option) => option.title}
												onChange={(optionValue: any) => {
													formik.setFieldValue('salaryType', optionValue?.value);
												}}
												onBlur={formik.handleBlur}
												value={salarytype?.find(
													(salaryValue: any) => salaryValue.value === formik.values.salaryType,
												)}
												autoFocus={false}
												placeholder={translate('PlaceHolder.selectSalaryType')}
												className="form-react-select"
												classNamePrefix="select"
											/>
											{renderError('salaryType')}
										</Form.Group>
									</Col>
									<Col xs={6} md={3}>
										<Form.Group className="mb-4 form-group position-relative">
											<Form.Label className="lh-1">{translate('Job.minimumSalary')}</Form.Label>
											<Form.Control
												type="number"
												placeholder={translate('PlaceHolder.enterMinSalary')}
												name="minAmount"
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.minAmount ?? 0}
												autoComplete="off"
												className="form-react-select"
												min={1}
											/>
											{renderError('minAmount')}
										</Form.Group>
									</Col>
									<Col xs={6} md={3}>
										<Form.Group className="form-group position-relative">
											<Form.Label className="lh-1">{translate('Job.maximumSalary')}</Form.Label>
											<Form.Control
												type="number"
												placeholder={translate('PlaceHolder.enterMaxSalary')}
												name="maxAmount"
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.maxAmount ?? 0}
												autoComplete="off"
												className="form-react-select"
												min={1}
											/>
											{renderError('maxAmount')}
										</Form.Group>
									</Col>
									<Col md={12}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">Skills</Form.Label>
											{(!id || formik.values.skills) && (
												<ReactSelect
													isSearchable
													name="skills"
													options={skill}
													getOptionValue={(option) => option.id}
													getOptionLabel={(option) => option.title}
													onChange={(optionValue: any) => {
														const valuesArr = [];
														if (formik.values.skills?.length) {
															valuesArr.push(...formik.values.skills, optionValue?.id?.toString());
														} else {
															valuesArr.push(optionValue?.id?.toString());
														}
														formik.setFieldValue('skills', valuesArr);
													}}
													onBlur={formik.handleBlur}
													autoFocus={false}
													placeholder={translate('PlaceHolder.addSkills')}
													value="Add Skills"
													className="form-react-select"
													classNamePrefix="select"
													isOptionDisabled={(option: any) =>
														formik.values.skills.some((item: any) => Number(item) === option?.id)
													}
												/>
											)}
											{renderError('skills')}
										</Form.Group>
										{(formik.values.skills?.length > 0 || formik.values.newRole?.length > 0) && (
											<div className="selected-items-list d-flex flex-wrap gap-3 mb-4">
												{[...formik.values.skills, ...formik.values.newRole].map((skillId: any) => (
													<div
														key={skillId?.id}
														className="selected-item-box d-flex align-items-center gap-2 rounded-pill"
													>
														<p className="mb-0">{getNameById(skill, skillId, 'title')}</p>
														<Button
															variant="link"
															className="p-0 text-decoration-none border-0"
															onClick={() => {
																const updatedSkills = formik.values.skills.filter(
																	(skillValue: any) => skillValue !== skillId,
																);
																formik.setFieldValue('skills', updatedSkills);
															}}
														>
															<i className="ri-close-line"></i>
														</Button>
													</div>
												))}
											</div>
										)}
									</Col>
									<Col md={12}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('Job.description')}</Form.Label>
											<Form.Control
												as="textarea"
												rows={4}
												placeholder={translate('PlaceHolder.writeHere')}
												name="description"
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													let inputValue = e.target.value;

													// Remove leading spaces if present
													inputValue = inputValue.replace(/^\s+/, '');

													// Allow only one space between words
													inputValue = inputValue.replace(/\s{2,}/g, ' ');
													formik.setFieldValue(`description`, inputValue);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.description}
												autoComplete="off"
											/>
											{renderError('description')}
										</Form.Group>
									</Col>
									<Col md={12}>
										<Button
											disabled={isPending}
											className="filled-btn rounded-pill mt-2"
											type="submit"
										>
											{translate('Job.saveProceed')}
										</Button>
									</Col>
								</Row>
							</Form>
						)}
					</div>
				</div>
			</Container>
			<JobAiSuggestions
				onOpenAIResponse={handleOpenAIResponse}
				updateSalaryFunc={updateSalaryFunc}
			/>
		</section>
	);
};
